// Netzwerkprotokoll. Ein flaches JSON-Format mit kurzen Feldnamen —
// Klartext ist beim Debuggen zu wertvoll, um es früh gegen Binär zu tauschen.
// Konzept §13: keine Accounts. Identität ist eine zufällige Session-ID.
// Schreiben ins Gedächtnis der Community braucht Schreibrecht (canWrite);
// Lesen und Mitfahren sind immer frei.

import { PROTOCOL_VERSION } from './constants.js';

export const MSG = Object.freeze({
  // Client -> Server
  HELLO: 'hello',
  PING: 'ping',
  INPUT: 'input',          // Positions-/Zustandsupdate des Spielers
  PAINT_STROKE: 'paint',   // ein fertiger Strich
  PAINT_SUB: 'psub',       // Interesse an Flächen anmelden
  RADIO_TX: 'rtx',         // Funkspruch senden
  RADIO_TUNE: 'rtune',     // Kanal wechseln
  RTC_SIGNAL: 'rtc',       // WebRTC-Signalling (Sprachfunk, später)
  PHOTO: 'photo',          // Benching: Foto gemeldet
  REPORT: 'report',        // Fläche melden (Konzept §13: Moderation nach Report)

  // Server -> Client
  WELCOME: 'welcome',
  PONG: 'pong',
  SNAPSHOT: 'snap',        // Weltzeit, Zug, Remote-Spieler
  PAINT_DATA: 'pdata',     // alle Striche einer Fläche
  PAINT_ADD: 'padd',       // neuer Strich von jemand anderem
  RADIO_RX: 'rrx',
  PEER_LEFT: 'pleft',
  ACCESS: 'access',        // Schreibrecht-Update (nach Token-Claim)
  ERROR: 'err',
});

/** Fehlercodes, die der Client kennt und dem Spieler erklären kann. */
export const ERR = Object.freeze({
  NO_WRITE: 'no_write',     // Lesen frei, Schreiben nicht (§13)
  BANNED: 'banned',         // Session gesperrt (nach Report)
  BAD_STROKE: 'bad_stroke', // Validierung
});

/** Session-ID: 8 Zeichen, kollisionsarm genug für hunderte Spieler. */
export function makeSessionId() {
  const chars = 'abcdefghijkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < 8; i++) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

export function encode(msg) {
  return JSON.stringify(msg);
}

export function decode(raw) {
  try {
    const msg = JSON.parse(typeof raw === 'string' ? raw : raw.toString('utf8'));
    if (!msg || typeof msg.t !== 'string') return null;
    return msg;
  } catch {
    return null;
  }
}

// --- Nachrichten-Konstruktoren ----------------------------------------------

/**
 * @param {number} clientTime
 * @param {string|null} [displayHint]  ungenutzt (kein Name im UI, §8)
 * @param {string|null} [writeToken]   Schreibschlüssel für provider=token
 */
export function hello(clientTime, displayHint = null, writeToken = null) {
  const msg = { t: MSG.HELLO, v: PROTOCOL_VERSION, ct: clientTime, hint: displayHint };
  if (writeToken) msg.tok = writeToken;
  return msg;
}

export function welcome({ sessionId, worldSeconds, serverTime, seed, seasonId, players, canWrite = true }) {
  return {
    t: MSG.WELCOME, v: PROTOCOL_VERSION,
    id: sessionId, ws: worldSeconds, st: serverTime,
    seed, season: seasonId, players,
    // w: Schreibrecht. Lesen ist immer erlaubt; ohne w darf der Client nicht malen.
    w: canWrite ? 1 : 0,
  };
}

/** Server teilt mit, ob diese Session schreiben darf (nach Token oder Sperre). */
export function access(canWrite) {
  return { t: MSG.ACCESS, w: canWrite ? 1 : 0 };
}

export function ping(clientTime) {
  return { t: MSG.PING, ct: clientTime };
}

export function pong(clientTime, serverTime, worldSeconds) {
  return { t: MSG.PONG, ct: clientTime, st: serverTime, ws: worldSeconds };
}

/**
 * Spieler-Input. Wir schicken die vom Client berechnete Position (Bewegung
 * bleibt clientseitig, weil es kein PvP gibt und Cheaten niemandem was nimmt —
 * Konzept §10). Der Server plausibilisiert nur grob.
 */
export function input({ x, y, z, yaw, pitch, anim, carId, warmth, wetness }) {
  return {
    t: MSG.INPUT,
    p: [round2(x), round2(y), round2(z)],
    r: [round3(yaw), round3(pitch)],
    a: anim ?? 0,
    c: carId ?? null,
    w: warmth != null ? Math.round(warmth) : undefined,
    q: wetness != null ? Math.round(wetness) : undefined,
  };
}

export function snapshot({ worldSeconds, serverTime, train, players }) {
  return { t: MSG.SNAPSHOT, ws: worldSeconds, st: serverTime, tr: train, pl: players };
}

/**
 * Ein Graffiti-Strich. Punkte sind normiert (0..1) auf die Fläche, damit die
 * Auflösung der Textur später änderbar bleibt.
 * pts: flaches Array [u0,v0,u1,v1,...] mit 3 Dezimalstellen.
 *
 * Herkunft (`at`) setzt der Server aus der Spielerposition — der Client
 * schickt sie nicht mit und darf sie nicht als Text anzeigen (§4/§8).
 */
export function paintStroke({ surfaceId, color, width, pts, tool = 'spray', worldSeconds }) {
  return {
    t: MSG.PAINT_STROKE,
    s: surfaceId, c: color, w: round3(width), pts, tl: tool, ws: worldSeconds,
  };
}

export function paintAdd(stroke) {
  return { t: MSG.PAINT_ADD, st: stroke };
}

export function paintData(surfaceId, strokes) {
  return { t: MSG.PAINT_DATA, s: surfaceId, strokes };
}

export function paintSub(surfaceIds) {
  return { t: MSG.PAINT_SUB, s: surfaceIds };
}

export function radioTx(channel, text) {
  return { t: MSG.RADIO_TX, ch: channel, m: text };
}

export function radioRx({ channel, text, quality, fromId, worldSeconds }) {
  return { t: MSG.RADIO_RX, ch: channel, m: text, q: round2(quality), f: fromId, ws: worldSeconds };
}

export function radioTune(channel) {
  return { t: MSG.RADIO_TUNE, ch: channel };
}

export function rtcSignal(targetId, payload) {
  return { t: MSG.RTC_SIGNAL, to: targetId, d: payload };
}

export function peerLeft(sessionId) {
  return { t: MSG.PEER_LEFT, id: sessionId };
}

/**
 * Fläche melden (Konzept §13). Keine Inhaltsprüfung auf dem Client —
 * der Server schreibt nur den Report; Löschen ist Betreibersache.
 */
export function report(surfaceId, reason = null) {
  const msg = { t: MSG.REPORT, s: surfaceId };
  if (reason != null && reason !== '') msg.r = String(reason).slice(0, 200);
  return msg;
}

export function errorMsg(code, detail = null) {
  return { t: MSG.ERROR, code, detail };
}

// --- Hilfen -----------------------------------------------------------------

function round2(v) { return Math.round(v * 100) / 100; }
function round3(v) { return Math.round(v * 1000) / 1000; }

/**
 * Strich ohne Autorenfeld und ohne interne Felder.
 * `by` verlässt den Server nie (Konzept §8).
 * `at` (Bogenlänge am Entstehungsort, §6.1) darf mit — es ist keine Identität,
 * sondern Rohdaten für spätere Verwitterungsdarstellung. Niemals als Text/UI.
 */
export function publicStroke(stroke) {
  if (!stroke || typeof stroke !== 'object') return stroke;
  const out = {
    id: stroke.id,
    s: stroke.s,
    c: stroke.c,
    w: stroke.w,
    pts: stroke.pts,
    tl: stroke.tl,
    ws: stroke.ws,
  };
  if (stroke.ts != null) out.ts = stroke.ts;
  if (stroke.seed) out.seed = true;
  // Herkunft nur wenn vorhanden (alte Striche ohne Feld bleiben gültig).
  if (stroke.at != null && Number.isFinite(stroke.at)) out.at = stroke.at;
  // absichtlich kein `by`
  return out;
}

/** Animationszustände als Zahl, damit Snapshots klein bleiben. */
export const ANIM = Object.freeze({
  IDLE: 0, WALK: 1, RUN: 2, CROUCH: 3, JUMP: 4, FALL: 5,
  CLIMB: 6, HANG: 7, RIDE_SIT: 8, PAINT: 9, DOWN: 10,
});

export const ANIM_NAMES = Object.freeze(
  Object.fromEntries(Object.entries(ANIM).map(([k, v]) => [v, k.toLowerCase()]))
);
